export interface DependencyData {
  id: string;
  functionInstanceId: string;
  operationName: string;
  partitionKey: string;
  dependencyName: string;
  metaData: { [key: string]: unknown };
  metaDataAsString: string;
  status: DependencyStatus;
  started: Date;
  completed: Date;
  suspectedStuckAt?: Date;
}

export enum DependencyStatus {
  Waiting = 0,
  Completed = 1,
  Failed = 2,
  Notified = 3,
  Terminated = 4,
  Abandoned = 5
}

export class DependencyCheckClient{
  dependencyKey: string;
  client: unknown;

  constructor(partitionKey:string, client:unknown){
    this.dependencyKey = partitionKey;
    this.client = client;
  }
}

export class DependencyCheckClientAgent{
  dependencyKey: string;
  clientAgent: unknown;

  constructor(partitionKey:string, clientAgent:unknown){
    this.dependencyKey = partitionKey;
    this.clientAgent = clientAgent;
  }
}

export class RestorePendingDependencies{
  appName: unknown;
  clientAgent: unknown;
  client: unknown;
  schemeId: unknown;
  type: unknown;
  valuationDate: unknown;

  constructor(
    appName: unknown,  
    clientAgent: unknown,
    client: unknown,
    schemeId: unknown,
    type: unknown,
    valuationDate: unknown)
    {
      this.appName = appName;
      this.clientAgent = clientAgent;
      this.client = client;
      this.schemeId = schemeId;
      this.type = type;
      this.valuationDate = valuationDate;
    }
}

/**
 * Request body for the manual Daily Valuation Kickoff endpoint (PMDEV-2198).
 * At least one of the three calculation types must be selected. runUpUntil is an
 * optional run-date override (non-prod usage) as an ISO date string (yyyy-MM-dd).
 */
export interface DailyValuationKickoffRequest {
  liability: boolean;
  asset: boolean;
  metrics: boolean;
  runUpUntil?: string | null;
}

/**
 * Request model for Check Is Ready API endpoint.
 * Includes routing information for Service Bus subscription filtering.
 */
export interface CheckIsReadyRequest {
  /** The partition key (dependency key) to check */
  partitionKey: string;
  /** The AppName (e.g., "pfaroechan", "pfaroepunt") - mapped to Instance for routing */
  appName?: string | null;
  /** Optional ClientAgentId for more specific routing (e.g., 46 for MercerDS) */
  clientAgentId?: number | null;
  /** The operation the user selected in the UI (PMDEV-3207). Un-parks that operation's
   * outstanding sibling dependency keys in addition to the checked key. */
  functionInstanceId?: string | null;
}

/**
 * Helper function to create CheckIsReadyRequest from DependencyData
 * Uses key existence check to preserve null values (rather than falling through with ??)
 */
export function toCheckIsReadyRequest(dependency: DependencyData): CheckIsReadyRequest {
  const metaData = dependency.metaData ?? {};

  // Check if uppercase key exists (even if null), otherwise fall back to lowercase
  const appName = "AppName" in metaData
    ? metaData["AppName"] as string | null
    : metaData["appName"] as string | undefined;

  const clientAgentId = "ClientAgentId" in metaData
    ? metaData["ClientAgentId"] as number | null
    : metaData["clientAgentId"] as number | undefined;

  return {
    partitionKey: dependency.partitionKey,
    appName,
    clientAgentId,
    functionInstanceId: dependency.functionInstanceId
  };
}

/**
 * Filter criteria for running actions on dependencies without loading individual rows.
 * Used by the "Check All Filtered" and group-level actions.
 */
export interface CheckByFilterRequest {
  status?: string[];
  appName?: string;
  type?: string;
  valuationDate?: string;
  valuationDateFrom?: string;
  valuationDateTo?: string;
  client?: string;
  clientAgent?: string;
  schemeId?: string;
  scheme?: string;
  location?: string;
  dependencyType?: string;
  dependencyName?: string[];
}

/** Level a group Force is applied at (PMDEV-3736). */
export type ForceMode = "Client" | "ClientAgent";

/**
 * Response of POST skipClient(Agent)DependencyCheck/byFilter. Same shape for a dry run and
 * an execute.
 */
export interface ForceByFilterResponse {
  mode: ForceMode;
  dryRun: boolean;
  /** Distinct (key, target) pairs = messages published (or to be published). */
  count: number;
  /** Eligible rows with a target ("dependencies to force"). */
  rows: number;
  dependencyKeys: number;
  /** Eligible rows with no Client / ClientAgent; never forced. */
  skippedMissingTarget: number;
  message: string;
}

/** Result of the forceByFilter thunk: the response, or the API's error text. */
export interface ForceByFilterOutcome {
  data?: ForceByFilterResponse;
  error?: string;
}

/** Request body for POST api/dependencies/statusCounts (PMDEV-3760). At most 100 keys. */
export interface StatusCountsRequest {
  partitionKeys: string[];
}

/** Row counts by status name for one dependency key. */
export interface StatusCounts {
  Waiting: number;
  Notified: number;
  Failed: number;
  Completed: number;
  Terminated: number;
  Abandoned: number;
}

export interface KeyStatusCounts {
  partitionKey: string;
  counts: StatusCounts;
  /** Waiting + Notified + Failed + Completed: the denominator of the progress share. */
  active: number;
  /** All statuses, including Terminated and Abandoned. */
  total: number;
  /** Earliest completion on the key (ISO, server time); null if none. */
  firstCompletedAt: string | null;
}

export interface StatusCountsResponse {
  /** Server time the counts were read (ISO). */
  asOf: string;
  /** Completed rows are kept at least this many days (Housekeeper DaysToKeep). */
  completedRetentionDays: number;
  keys: KeyStatusCounts[];
}